import { Accordion, AccordionItem, TextInput, TextArea } from '@carbon/react';
import type { AreaDetailSectionProps } from './types';
import styles from './area-detail-section.module.scss';

/**
 * Shared "Area Details" section for all scenario form types.
 * Two-column layout: left column has user-defined children (geometry picker, etc.)
 * plus a common Alias field; right column has a common Notes textarea.
 * When `collapsible` is set, the section is wrapped in an accordion (open by
 * default) so the heading doubles as a collapse toggle.
 * @param {AreaDetailSectionProps} props - The component props
 * @return {JSX.Element} The area detail section
 */
export function AreaDetailSection({
  idPrefix = 'area',
  aliasValue,
  onAliasChange,
  notesValue,
  onNotesChange,
  notesPlaceholder = 'Notes about this area...',
  collapsible = false,
  children,
}: AreaDetailSectionProps): JSX.Element {
  const body = (
    <div className={styles.twoColumn}>
      <div className={styles.leftCol}>
        {children}

        <div className={styles.aliasField}>
          <TextInput
            id={`${idPrefix}-alias`}
            labelText="Alias"
            placeholder="Optional display name"
            size="md"
            light
            value={aliasValue}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              onAliasChange(e.target.value)
            }
          />
        </div>
      </div>

      <div className={styles.rightCol}>
        <h4 className={styles.sectionTitle}>Notes</h4>
        <TextArea
          id={`${idPrefix}-notes`}
          labelText=""
          hideLabel
          placeholder={notesPlaceholder}
          rows={6}
          light
          value={notesValue}
          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
            onNotesChange(e.target.value)
          }
        />
      </div>
    </div>
  );

  if (collapsible) {
    return (
      <Accordion className={styles.areaDetails}>
        <AccordionItem title="Area Details" open>
          {body}
        </AccordionItem>
      </Accordion>
    );
  }

  return (
    <div className={styles.areaDetails}>
      <h4 className={styles.sectionTitle}>Area Details</h4>
      {body}
    </div>
  );
}
